import i18next from 'i18next'
import * as React from 'react'
import { Link, Updater, Widget, fromJSX } from 'widgets-for-react'
import { jobWidget } from '../../forms/job/jobWidget'
import { routeUpdaters } from '../../site/root/routes/routesState'
import { DataLayer } from '../../utils/datalayer'
import {
  AuthButtonPrimary,
  FormControls,
  FormNotification,
  FormSkeletonLayout,
  formSkeletonConfig,
} from '../../utils/forms'
import { singleValidatorResultBuilders } from '../../validator/validator'
import { Api } from './api_to_state'
import { AuthState, SignUpState, isSignUpState } from './authState'
import { getClassName, isValidEmail, options } from './utils'

export const emptyHtml: JSX.Element = <></>

export function signup_form(
  options: options,
  api: Api,
  readonly: boolean,
  s0: SignUpState,
  onTermsAndConditions: JSX.Element,
  displayNotification: boolean,
  type: 'regular' | 'student' | 'promo',
  suppressHotjar: boolean
): Widget<Updater<AuthState>> {
  const showFieldErrors = s0.signUpState !== 'sign up'
  const nameError = !s0.login_data.name
  const surnameError = !s0.login_data.surname
  const emailError =
    !s0.login_data.email || !isValidEmail(s0.login_data.email)
  const emailBackendError = s0.signUpState === 'sign up - error invalid email'
  const passwordScore = api.scorePassword(s0.login_data.password || '').res
  const passwordScoreError =
    api.scorePassword(s0.login_data.password || '').res !== 'strong' ||
    s0.signUpState === 'sign up - error weak password'
  const termsAndConditionsError = !s0.login_data.termsAndConditions
  const accountExistsError =
    s0.signUpState === 'sign up - error account already exists'
  const jobsError =
    type === 'student'
      ? false
      : s0.login_data.selectedJob.validatorResult.path('Id').kind !== 'passed'
  return fromJSX((setState: (_: Updater<AuthState>) => void) => (
    <FormSkeletonLayout
      type="sign-up"
      kind="text-image"
      onSubmit={(e) => {
        e.preventDefault()
        if (nameError) DataLayer.errorOnTrialForm('first name not filled in')
        if (surnameError) DataLayer.errorOnTrialForm('last name not filled in')
        if (emailError)
          DataLayer.errorOnTrialForm('email not filled in or not valid')
        if (passwordScoreError)
          DataLayer.errorOnTrialForm('password not filled in or too weak')
        if (termsAndConditionsError)
          DataLayer.errorOnTrialForm('terms and conditions not accepted')
        if (jobsError) DataLayer.errorOnTrialForm('job not selected')
        setState((s) =>
          isSignUpState(s)
            ? !nameError &&
              !surnameError &&
              !emailError &&
              !passwordScoreError &&
              !termsAndConditionsError &&
              !jobsError
              ? type === 'promo'
                ? {
                    ...s,
                    signUpState: 'sign up - promo code',
                  }
                : { ...s, signUpState: 'sign up - signing up' }
              : {
                  ...s,
                  signUpState: 'sign up - error form validation',
                  login_data: {
                    ...s.login_data,
                    name: s.login_data.name || '',
                    surname: s.login_data.surname || '',
                    email: s.login_data.email || '',
                    password: s.login_data.password || '',
                  },
                }
            : s
        )
      }}
      options={options}
      isClrInverted={true}
      image={formSkeletonConfig.imageDark}
    >
      <header className={getClassName(options, 'headerClassName', 'mb-4')}>
        <h1 className={getClassName(options, 'titleClassName', 'mb-4')}>
          {type === 'regular'
            ? i18next.t('subscription:registerTitle')
            : type === 'student'
            ? i18next.t('studentSubscription:registerTitle')
            : i18next.t('promoCode:step1Title')}
        </h1>
        {type === 'regular' && (
          <p className={getClassName(options, 'introClassName', 'lead')}>
            {i18next.t('subscription:registerIntro')}
          </p>
        )}
        {type === 'student' && (
          <p className={getClassName(options, 'introClassName', 'lead')}>
            {i18next.t('studentSubscription:registerIntro')}
          </p>
        )}

        {type === 'promo' && (
          <>
            <p className={getClassName(options, 'introClassName', 'lead')}>
              {i18next.t('promoCode:step1IntroA')}
            </p>
            <p className={getClassName(options, 'introClassName', 'lead')}>
              {i18next.t('promoCode:step1IntroB')}
            </p>
            <p className={getClassName(options, 'introClassName', 'lead')}>
              {i18next.t('promoCode:step1IntroC')}
            </p>
          </>
        )}
      </header>

      {/* Notification */}
      {displayNotification && (
        <FormNotification
          options={options}
          message={
            s0.signUpState === 'sign up - error too many attempts'
              ? i18next.t('auth:signupErrorTooManyAttempts')
              : i18next.t('auth:defaultErrorNotification')
          }
        />
      )}

      {/* Name */}
      <FormControls.FormItemInGroup
        controlId="firstName"
        label={i18next.t('auth:signupName')}
        type="text"
        readOnly={readonly}
        required={true}
        value={s0.login_data.name || ''}
        onChange={(newValue) =>
          setState((s) =>
            isSignUpState(s)
              ? {
                  ...s,
                  login_data: {
                    ...s.login_data,
                    name: newValue,
                  },
                }
              : s
          )
        }
        onBlur={() => DataLayer.changeTrialFormValue('name')}
        validation={
          showFieldErrors && nameError
            ? singleValidatorResultBuilders.failed(
                'name',
                i18next.t('auth:signupNameError')
              )
            : singleValidatorResultBuilders.passed('name')
        }
        suppressHotjar={suppressHotjar}
      />

      {/* Surname */}
      <FormControls.FormItemInGroup
        controlId="surname"
        label={i18next.t('auth:signupSurname')}
        type="text"
        readOnly={readonly}
        required={true}
        value={s0.login_data.surname || ''}
        onChange={(newValue) =>
          setState((s) =>
            isSignUpState(s)
              ? {
                  ...s,
                  login_data: {
                    ...s.login_data,
                    surname: newValue,
                  },
                }
              : s
          )
        }
        onBlur={() => DataLayer.changeTrialFormValue('surname')}
        validation={
          showFieldErrors && surnameError
            ? singleValidatorResultBuilders.failed(
                'surname',
                i18next.t('auth:signupSurnameError')
              )
            : singleValidatorResultBuilders.passed('surname')
        }
        suppressHotjar={suppressHotjar}
      />

      {/* E-mail address */}
      <FormControls.FormItemInGroup
        controlId="email"
        label={i18next.t('auth:email')}
        type="text"
        inputMode="email"
        readOnly={readonly}
        required={true}
        value={s0.login_data.email || ''}
        onChange={(newValue) =>
          setState((s) =>
            isSignUpState(s)
              ? {
                  ...s,
                  signUpState:
                    s.signUpState === 'sign up - error account already exists'
                      ? 'sign up'
                      : s.signUpState,
                  login_data: {
                    ...s.login_data,
                    email: newValue.toLowerCase(),
                  },
                }
              : s
          )
        }
        onBlur={() => {
          setState((s) =>
            isSignUpState(s) &&
            s.login_data.email &&
            !isValidEmail(s.login_data.email)
              ? {
                  ...s,
                  signUpState: 'sign up - error form validation',
                }
              : s
          )
          DataLayer.changeTrialFormValue('email')
        }}
        validation={
          showFieldErrors && (emailError || emailBackendError)
            ? singleValidatorResultBuilders.failed(
                'email',
                i18next.t('auth:signupEmailError')
              )
            : accountExistsError
            ? singleValidatorResultBuilders.failed(
                'email',
                <>{i18next.t('auth:signupEmailExistsErrorA')} <Link to={routeUpdaters(i18next.language).login.url}>{i18next.t('auth:signupEmailExistsErrorB')}</Link></>
              )
            : singleValidatorResultBuilders.passed('email')
        }
        suppressHotjar={suppressHotjar}
      />

      {/* Password */}
      <FormControls.FormItemInGroup
        controlId="password"
        label={i18next.t('auth:password')}
        type={s0.login_data.showPassword ? 'text' : 'password'}
        readOnly={readonly}
        required={true}
        value={s0.login_data.password}
        passwordScore={passwordScore}
        onChange={(newValue) =>
          setState((s) =>
            isSignUpState(s)
              ? {
                  ...s,
                  login_data: {
                    ...s.login_data,
                    password: newValue,
                  },
                }
              : s
          )
        }
        onBlur={() => DataLayer.changeTrialFormValue('password')}
        validation={
          showFieldErrors && passwordScoreError
            ? singleValidatorResultBuilders.failed(
                'password',
                i18next.t('auth:weakPassword')
              )
            : singleValidatorResultBuilders.passed('password')
        }
        showPasswordToggle={{
          isChecked: s0.login_data.showPassword,
          label: i18next.t(
            s0.login_data.showPassword
              ? 'auth:hidePasswordShort'
              : 'auth:showPasswordShort'
          ),
          title: i18next.t(
            s0.login_data.showPassword
              ? 'auth:hidePassword'
              : 'auth:showPassword'
          ),
          onChange: (newValue) =>
            setState((s) =>
              isSignUpState(s)
                ? {
                    ...s,
                    login_data: {
                      ...s.login_data,
                      showPassword: newValue,
                    },
                  }
                : s
            ),
        }}
        suppressHotjar={suppressHotjar}
      />

      {/* Job select */}
      {s0.login_data.jobs.kind == 'loaded' &&
        jobWidget(
          s0.login_data.jobs.value,
          s0.signUpState === 'sign up - error form validation',
          readonly,
          'trial',
          type === 'student',
          suppressHotjar
        )(s0.login_data.selectedJob)
          .map(
            (updater) => (s: AuthState) =>
              s.status == 'logged out' && isSignUpState(s)
                ? {
                    ...s,
                    login_data: {
                      ...s.login_data,
                      selectedJob: updater(s.login_data.selectedJob),
                    },
                  }
                : s
          )
          .run(setState)}

      {/* Marketing mails */}
      <FormControls.FormCheckInGroup
        controlId="marketingEmails"
        label={i18next.t('subscription:registerSignUpWithMarketing')}
        readOnly={readonly}
        required={false}
        checked={s0.login_data.marketingEmails}
        onChange={(newValue) =>
          setState((s) =>
            isSignUpState(s)
              ? {
                  ...s,
                  login_data: {
                    ...s.login_data,
                    marketingEmails: newValue,
                  },
                }
              : s
          )
        }
        validation={undefined}
        suppressHotjar={suppressHotjar}
      />

      {/* Terms & Conditions */}
      <FormControls.FormCheckInGroup
        controlId="termsAndConditions"
        label={
          <>
            {i18next.t('auth:signupTermsAndConditionsPre')}{' '}
            {onTermsAndConditions}
          </>
        }
        readOnly={readonly}
        required={true}
        checked={s0.login_data.termsAndConditions}
        onChange={(newValue) =>
          setState((s) =>
            isSignUpState(s)
              ? {
                  ...s,
                  login_data: {
                    ...s.login_data,
                    termsAndConditions: newValue,
                  },
                }
              : s
          )
        }
        validation={
          showFieldErrors && termsAndConditionsError
            ? singleValidatorResultBuilders.failed(
                'termsAndConditions',
                i18next.t('auth:signupTermsAndConditionsError')
              )
            : singleValidatorResultBuilders.passed('termsAndConditions')
        }
        suppressHotjar={suppressHotjar}
      />

      {/* Submit */}
      <div className={getClassName(options, 'formActionsClassName', '')}>
        <div className={getClassName(options, 'clickableWrapperClassName', '')}>
          <AuthButtonPrimary
            kind="button"
            type="submit"
            disabled={readonly || s0.login_data.jobs.kind != 'loaded'}
            label={
              readonly ? (
                <FormControls.SubmittingSpinner />
              ) : type === 'promo' ? (
                i18next.t('promoCode:step1Submit')
              ) : (
                i18next.t('auth:signupSubmit')
              )
            }
            onClick={() => null}
          />
        </div>
      </div>
    </FormSkeletonLayout>
  ))
}
